<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const isEnabled = ref(false);
const isOutgoingEnabled = ref(false);

const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { audio_transcriptions, transcribe_outgoing_messages } = currentAccount.value?.settings || {};
    isEnabled.value = !!audio_transcriptions;
    // Default to false if not configured (strict true check)
    isOutgoingEnabled.value = transcribe_outgoing_messages === true || transcribe_outgoing_messages === 'true';
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    await updateAccount(settings);
    useAlert(t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.API.ERROR'));
  }
};

const toggleAudioTranscription = async () => {
  return updateAccountSettings({
    audio_transcriptions: isEnabled.value,
    // Keep outgoing state when toggling main switch
    transcribe_outgoing_messages: isOutgoingEnabled.value,
  });
};

const toggleOutgoingTranscription = async () => {
  return updateAccountSettings({
    transcribe_outgoing_messages: isOutgoingEnabled.value,
  });
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.NOTE')"
    tooltip-identifier="settings_audio_transcription"
    with-border
  >
    <template #headerActions>
      <div class="flex flex-col gap-4 items-end">
        <div class="flex items-center justify-end gap-3">
          <span v-if="isEnabled" class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.TOGGLE_LABEL') || 'Ativar Transcrição' }}
          </span>
          <Switch v-model="isEnabled" @change="toggleAudioTranscription" />
        </div>
        
        <div v-if="isEnabled && isSuperAdmin" class="flex items-center justify-end gap-3">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ t('GENERAL_SETTINGS.FORM.AUDIO_TRANSCRIPTION.OUTGOING_MESSAGE_LABEL') }}
          </span>
          <Switch v-model="isOutgoingEnabled" @change="toggleOutgoingTranscription" />
        </div>
      </div>
    </template>
  </SectionLayout>
</template>

